import { Stack, Typography, Box, Button } from '@mui/material';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { useSubmitSalesAttributionRequestMutation } from '@components/Dashboard/dashboardSlice/campaignsApi';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import upsellImage from '../../images/upsell.png';

type UpsellProps = {
  campaignId?: number;
  context?: 'sales' | 'visit';
  requested?: boolean;
};

export const Upsell = ({
  campaignId,
  context = 'visit',
  requested: initialRequested = false,
}: UpsellProps) => {
  const [submitSalesRequest] = useSubmitSalesAttributionRequestMutation();
  const [requested, setRequested] = useState(initialRequested);

  const title =
    context === 'sales'
      ? 'Uncover Sales Performance'
      : 'Uncover Visit Performance';
  const description =
    context === 'sales'
      ? 'Measure and analyze sales performance to generate comprehensive insights.'
      : 'Measure and analyze foot traffic performance to generate comprehensive insights.';

  const handleRequest = () => {
    if (!campaignId) return;
    const attributionType = context === 'sales' ? 'Sales' : 'Visit';
    try {
      void submitSalesRequest(campaignId).unwrap();
      setRequested(true);
      enqueueSnackbar(`${attributionType} Attribution Requested`, {
        variant: 'success',
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error requesting ${attributionType} Attribution`, {
        variant: 'error',
      });
    }
  };

  const handleClick = campaignId ? handleRequest : undefined;

  return (
    <Stack
      gap="2rem"
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: '600px',
        padding: '3rem 2rem',
        backgroundColor: 'white',
      }}
    >
      <Stack gap="1.5rem" alignItems="center" textAlign="center">
        <Box
          component="img"
          src={upsellImage}
          alt="Upsell"
          sx={{
            width: '464px',
            height: '259px',
            objectFit: 'contain',
          }}
        />

        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: unifiedPlatformColorPalette.lightTextPrimary,
            fontSize: '24px',
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: unifiedPlatformColorPalette.lightTextSecondary,
            maxWidth: '480px',
            lineHeight: 1.6,
            fontSize: '15px',
          }}
        >
          {description}
        </Typography>

        <Button
          onClick={handleClick}
          disabled={requested || !campaignId}
          variant="contained"
          sx={{
            backgroundColor: requested
              ? unifiedPlatformColorPalette.lightTextSecondary
              : unifiedPlatformColorPalette.inmarketGreen,
            color: unifiedPlatformColorPalette.white,
            padding: '12px 28px',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 600,
            marginTop: '0.5rem',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: requested
                ? unifiedPlatformColorPalette.lightTextSecondary
                : unifiedPlatformColorPalette.lightPrimaryDark,
            },
          }}
        >
          {requested
            ? 'Request submitted to your Account Manager'
            : 'Discover More'}
        </Button>
      </Stack>
    </Stack>
  );
};
